/**
 * Block 12 PR-B Phase B3 Part C Step 17 — SectionMaterials (Б).
 *
 * Renders MaterialRow[] из `computeMaterialRows` (Step 13) через `<ResultRow>`
 * (Step 15) с жёлтой кнопкой «Найти и узнать цену» (deeplink, ADR-0099).
 *
 * **Hotfix-2C Phase C2 Step 5 (2026-05-17):** «Тип открывания фасадов» / «Тип
 * столешницы» / «Длина листа столешницы» переехали из Section Б (inline pickers)
 * в Section А (Параметры) per Schemas v2. SectionMaterials больше не рендерит
 * эти pickers — они теперь в methodology.fields[] и рендерятся через
 * SectionParams. State pipeline (params.handleType, params.countertopKind,
 * params.countertopSheetLen) сохраняется без изменений — formulas continue
 * читать из state.
 *
 * **Path 1 dispatch** в computeMaterialRows (Step 13) — этот section просто
 * рендерит результат. Если handleType='гола' → MaterialRow[] уже содержит
 * gola-profile строку (Kitchen), section показывает её.
 *
 * Reference: ADR-0020 §Q1 (template), §Q4 (override), §Q11 (deeplinks),
 * Schemas v2 «Кухня» / «Сан Узел: тумба» / «ТВ-зона».
 */

import { type CalculatorAnketaType } from '@pomoshnik/shared';
import { SectionCard } from '../components/SectionCard';
import { SectionHeader } from '../components/SectionHeader';
import { ResultRow } from './ResultRow';
import type { AnketaParams, MaterialRow } from '../types';

export interface SectionMaterialsProps {
  anketaType: CalculatorAnketaType;
  /**
   * Params accepted для backward compat; больше не используется внутри после
   * Phase C2 Step 5 migration на Section А fields (inline pickers удалены).
   */
  params: AnketaParams;
  materialRows: MaterialRow[];
  /**
   * Hotfix-2C Phase C2 Step 5 — флаг обработан в Section А через methodology
   * JSON `countertopKind` field; параметр сохраняется для backward compat но
   * больше не активирует UI рендеринг внутри SectionMaterials.
   */
  showCountertop?: boolean;
  /**
   * onParamChange сохраняется в API для backward compat (callers продолжают
   * pass'ить). Внутри Phase C2+ не используется — Section А управляет params.
   */
  onParamChange: (field: string, value: unknown) => void;
  onMaterialPrice: (rowId: string, price: number) => void;
  onMaterialQty: (rowId: string, qty: number) => void;
  onResetMaterialAuto: (rowId: string) => void;
  onDeeplink?: (
    rowId: string,
    target: 'decor' | 'partner' | 'furniture' | 'handles',
  ) => void | Promise<void>;
  errorForPath: (path: string) => string | undefined;
  /**
   * PR-H1 Round 2 (ADR-0026) — per-row brand input. Container passes
   * `state.brandSelections` + `actions.setBrand`. ResultRow renders brand input
   * для каждой material row (computeMaterialRows.ts — все rows имеют deeplink
   * decor/furniture, поэтому brand input shown на всех). Optional чтобы legacy
   * tests / callers без brand wiring продолжали работать без brand input.
   */
  brandSelections?: Record<string, string>;
  onBrandChange?: (rowId: string, brand: string) => void;
}

export function SectionMaterials({
  materialRows,
  onMaterialPrice,
  onMaterialQty,
  onResetMaterialAuto,
  onDeeplink,
  brandSelections,
  onBrandChange,
}: SectionMaterialsProps) {
  return (
    <SectionCard testId="section-materials">
      <SectionHeader>Расчёт стоимости</SectionHeader>
      <div className="flex flex-col gap-4">
        {materialRows.length === 0 ? (
          <p className="text-muted text-sm">Заполните параметры — материалы пересчитаются.</p>
        ) : (
          materialRows.map((row) => (
            <div key={row.id} className="flex flex-col gap-2">
              <ResultRow
                row={row}
                onQtyChange={(qty) => onMaterialQty(row.id, qty)}
                onPriceChange={(price) => onMaterialPrice(row.id, price)}
                onResetAuto={() => onResetMaterialAuto(row.id)}
                onDeeplink={
                  row.deeplink && onDeeplink ? () => onDeeplink(row.id, row.deeplink!) : undefined
                }
                /* PR-H1 Round 2 (ADR-0026) — per-row brand input для material/furniture
                   rows. Render gated on `onBrandChange` — если caller не передал, brand
                   input не рендерится (ResultRow проверяет typeof onBrandChange). */
                brand={brandSelections?.[row.id]}
                onBrandChange={onBrandChange ? (b) => onBrandChange(row.id, b) : undefined}
              />
              {/* PR-D1 (2026-05-31) — «Убрать» обнуляет ТОЛЬКО Цену материала
                  (Кол-во/размеры — авто, не трогаем). Видна когда есть введённая
                  цена (> 0). Переиспользует SET_MATERIAL_PRICE(rowId, 0). */}
              {row.price > 0 ? (
                <button
                  type="button"
                  onClick={() => onMaterialPrice(row.id, 0)}
                  data-testid={`material-remove-${row.id}`}
                  className="min-h-tap rounded-xl border-[1.5px] border-accent bg-card text-muted font-semibold text-sm px-4 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none self-end"
                >
                  Убрать
                </button>
              ) : null}
            </div>
          ))
        )}
      </div>
    </SectionCard>
  );
}
